/**
 * 送金依頼 / 送金完了（transfer_requests）の hooks（v3.x / 2026-07）。
 *
 * - 一覧: 経理「送金依頼」タブ。status で絞込。書込は Callable のみ。
 * - 送金依頼追加（商談詳細 > 発注）/ 送金依頼を送信（経理へまとめて連絡）/
 *   prepare/complete（完了メール・メーカー宛）。
 */
import type {
  AccountingNotifyChannels,
  TransferRequest,
  TransferRequestStatus,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface TransferRequestRecord extends TransferRequest {
  id: string;
}

/** status（既定 'requested'）で transfer_requests を取得。 */
export function useTransferRequests(status: TransferRequestStatus = 'requested') {
  return useQuery({
    queryKey: ['transfer_requests', status],
    staleTime: 30_000,
    queryFn: async (): Promise<TransferRequestRecord[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'transfer_requests'),
          where('status', '==', status),
          orderBy('requestedAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as TransferRequest),
      }));
    },
  });
}

// ─── 送金依頼追加（商談詳細 > 発注）2026-10-26 ───

export type AccountingChannelOutcome = 'sent' | 'off' | 'no_target' | 'failed';

export interface AccountingNotifyOutcome {
  mail: AccountingChannelOutcome;
  googleChat: AccountingChannelOutcome;
  chatwork: AccountingChannelOutcome;
}

const addRequestFn = httpsCallable<
  { dealId: string; poId: string; manufacturerQuoteId?: string | null },
  { transferRequestId: string; alreadyExisted: boolean; notify: AccountingNotifyOutcome | null }
>(functions, 'addTransferRequest');

export function useAddTransferRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      poId: string;
      manufacturerQuoteId?: string | null;
    }) => {
      const r = await addRequestFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['transfer_requests'] });
      qc.invalidateQueries({
        queryKey: ['purchase_orders', 'deal', vars.dealId],
      });
    },
  });
}

// ─── 送金依頼を送信（経理へまとめて連絡）2026-10-26 ───

export interface TransferBatchGroup {
  categoryId: string | null;
  categoryName: string | null;
  count: number;
  newCount: number;
  totalLabel: string;
  text: { subject: string; chatwork: string; googleChat: string; plain: string };
  outcome: AccountingNotifyOutcome | null;
}

export interface TransferBatchResult {
  dryRun: boolean;
  channels: AccountingNotifyChannels;
  targets: { email: string; googleChatName: string | null; chatworkName: string | null };
  groups: TransferBatchGroup[];
  markedCount: number;
}

const batchFn = httpsCallable<{ dryRun: boolean }, TransferBatchResult>(
  functions,
  'sendTransferRequestsToAccounting',
);

/** dryRun=true: 送らずに文面と送信先を取得 / false: 送信して「送金依頼済み」を付ける。 */
export function useSendTransferRequestsToAccounting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dryRun: boolean }) => {
      const r = await batchFn(input);
      return r.data;
    },
    onSuccess: (d) => {
      if (!d.dryRun) qc.invalidateQueries({ queryKey: ['transfer_requests'] });
    },
  });
}

// ─── 送金完了メール（メーカー宛）prepare / complete ───

const prepareCompletionFn = httpsCallable<
  { requestIds: string[] },
  {
    defaultTo: string[];
    defaultCc: string[];
    fromAccountId: string | null;
    subject: string;
    body: string;
    count: number;
  }
>(functions, 'prepareTransferCompletionMail');

export function usePrepareTransferCompletionMail() {
  return useMutation({
    mutationFn: async (input: { requestIds: string[] }) => {
      const r = await prepareCompletionFn(input);
      return r.data;
    },
  });
}

const completeFn = httpsCallable<
  {
    requestIds: string[];
    to: string[];
    cc?: string[];
    subject: string;
    body: string;
    fromAccountId?: string | null;
    image?: {
      contentBase64: string;
      filename: string;
      contentType: string;
    } | null;
  },
  { ok: boolean; reason?: string; completedCount: number }
>(functions, 'completeTransferRequests');

export function useCompleteTransferRequests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NonNullable<Parameters<typeof completeFn>[0]>) => {
      const r = await completeFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer_requests'] });
    },
  });
}
